import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';

const TonsToKilogramsConverter = () => {
  const [inputData, setInputData] = useState({
    tons: '',
    kilograms: ''
  });

  const [results, setResults] = useState(null);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setInputData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const convertTonsToKilograms = () => {
    const { tons } = inputData;

    // Validate input
    if (!tons) {
      setError('Please enter a value in tons');
      return;
    }

    // Check if input is a valid number
    if (isNaN(parseFloat(tons)) || !isFinite(tons)) {
      setError('Please enter a valid number');
      return;
    }

    // Convert tons to kilograms
    const kilograms = parseFloat(tons) * 1000;
    const formattedKilograms = kilograms.toFixed(2);

    const calculationSteps = [
      `Step 1: Input value in tons: ${tons} t`,
      'Step 2: Use conversion factor (1 ton = 1000 kilograms)',
      `Step 3: Multiply tons by conversion factor: ${tons} * 1000`,
      `Step 4: Result in kilograms: ${formattedKilograms} kg`
    ];

    setResults({
      tons,
      kilograms: formattedKilograms,
      calculationSteps
    });
  };

  const exampleData = {
    tons: '2.5',
    kilograms: '2500'
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 1.5 }}
      className="min-h-screen bg-gradient-to-r from-purple-100 to-pink-100"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Helmet>
          <title>Tons to Kilograms Converter | Weight Conversion Tool</title>
          <meta name="description" content="Convert tons to kilograms easily with this calculator. This tool helps in converting weight measurements for logistics, shipping, and more." />
          <meta name="keywords" content="tons to kilograms, weight converter, logistics, shipping weight" />
        </Helmet>

        <div className="relative overflow-hidden bg-white rounded-lg shadow-2xl p-8 mb-8">
          <div className="calculator-header flex items-center mb-8">
            <div className="header-icon text-4xl text-pink-600 mr-4">⚖️</div>
            <h1 className="text-3xl font-bold text-gray-800">Tons to Kilograms Converter</h1>
          </div>

          <div className="text-gray-600 mb-8">
            <p>Convert tons to kilograms easily. This tool helps in converting weight measurements for logistics, shipping, and more.</p>
          </div>

          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="input-group">
              <label className="block text-sm font-medium text-gray-700 mb-2">Tons:</label>
              <input
                type="text"
                name="tons"
                value={inputData.tons}
                onChange={handleInputChange}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500 focus:border-transparent"
                placeholder="Enter weight in tons (e.g., 2.5)"
              />
            </div>
          </div>

          <button
            onClick={convertTonsToKilograms}
            className="w-full py-3 px-4 bg-pink-600 text-white font-medium rounded-lg hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors duration-200"
          >
            Convert
          </button>

          {error && (
            <div className="mt-4 p-4 text-red-700 bg-red-100 rounded-lg">
              {error}
            </div>
          )}

          {results && (
            <div className="mt-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Results</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-lg font-semibold text-gray-700 mb-4">Conversion Result</h3>
                  <p className="text-lg">Input Tons: {results.tons} t</p>
                  <p className="text-lg">Converted Kilograms: {results.kilograms} kg</p>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-700 mb-4">Calculation Steps</h3>
                <div className="space-y-4">
                  {results.calculationSteps.map((step, index) => (
                    <div key={index} className="p-4 bg-white rounded-lg shadow-inner">
                      <pre className="whitespace-pre-wrap font-mono text-gray-700">{step}</pre>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Example Calculation Section */}
        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Example Calculation <span className="text-blue-600">📝</span></h2>
          <div className="text-gray-600">
            <p>Here's an example calculation for converting tons to kilograms:</p>
            
            <div className="bg-white p-6 rounded-lg shadow-md mt-4">
              <h4 className="mb-4">Sample Data:</h4>
              <ul className="list-disc pl-6 mb-4">
                <li>Input Tons: {exampleData.tons} t</li>
                <li>Converted Kilograms: {exampleData.kilograms} kg</li>
              </ul>

              <h4 className="mb-4">Calculation Method:</h4>
              <ol className="list-decimal pl-6 mb-4">
                <li>Use the conversion factor: 1 ton = 1000 kilograms</li>
                <li>Multiply the input tons by the conversion factor</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">How to Use <span className="text-blue-600">📋</span></h2>
          <div className="text-gray-600">
            <p>Follow these steps to use the Tons to Kilograms Converter:</p>
            <ol className="list-decimal pl-6 mt-4">
              <li><strong>Enter Tons:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Input the weight in tons</li>
                </ul>
              </li>
              <li><strong>Click Convert:</strong> The converter will calculate:
                <ul className="list-disc pl-6 mt-2">
                  <li>The equivalent weight in kilograms</li>
                </ul>
              </li>
              <li><strong>Interpret Results:</strong>
                <ul className="list-disc pl-6 mt-2">
                  <li>Review the converted weight in kilograms</li>
                </ul>
              </li>
            </ol>
            <div className="mt-4 p-4 bg-blue-100 rounded-lg">
              <p><strong>💡 Pro Tips:</strong></p>
              <ul className="list-disc pl-6 mt-2">
                <li>Useful for logistics and shipping</li>
                <li>Helpful for industrial applications</li>
                <li>Always double-check your inputs</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Understanding Weight Conversion <span className="text-blue-600">💻</span></h2>
          <div className="text-gray-600">
            <p>
              Weight conversion is essential in various fields such as logistics, shipping, and industrial applications. It helps in:
            </p>
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Logistics:</strong> Ensuring accurate weight measurements for transportation</li>
              <li><strong>Shipping:</strong> Calculating package weights</li>
              <li><strong>Industry:</strong> Standardizing measurements</li>
            </ul>
          </div>
        </div>

        <div className="calculator-section mt-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6">Best Practices <span className="text-blue-600">✨</span></h2>
          <div className="text-gray-600">
            <ul className="list-disc pl-6 mt-4">
              <li><strong>Accuracy:</strong> Double-check your input values</li>
              <li><strong>Documentation:</strong> Record your conversions</li>
              <li><strong>Validation:</strong> Verify results when critical</li>
              <li><strong>Implementation:</strong> Follow system requirements</li>
            </ul>
            <p className="italic mt-4">
              Note: Always verify your conversions, especially when using them in critical applications.
            </p>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default TonsToKilogramsConverter;